import React, { useState, useEffect } from "react";
import { DropEvent, FileRejection, useDropzone } from "react-dropzone";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import AppText from "../../Core/AppText/AppText";
import Flex from "../../Layout/Flex";
import { appTheme } from "../../../theme/Theme";
import { useMediaStore } from "../../../stores/media.store";

interface FileUploadRule {
	validTypes: string[];
	maxQuantity: number;
	warningMessage: string;
	currentQuantity?: number;
}

interface FileImporterProps {
	dropZoneError?: string;
	maxFileSize?: number;
	maxFiles?: number;
	multipleFiles?: boolean;
	acceptedTypes?: string[];
	uploadRules?: FileUploadRule[];
	uploadedFileName?: string;
}

const FileImporter: React.FC<FileImporterProps> = ({
	dropZoneError,
	maxFileSize,
	maxFiles = 1,
	multipleFiles,
	acceptedTypes = [],
	uploadRules = [],
	uploadedFileName,
}) => {
	const { t } = useTranslation();

	const mediaStore = useMediaStore();

	const maxUploadFileSize = maxFileSize || 15000000;

	const [errorMessage, setErrorMessage] = useState<string>("");
	const [files, setFiles] = useState<any>([]);

	const { getRootProps, getInputProps } = useDropzone({
		// accept: "image/*, .pdf, .doc",
		maxFiles: maxFiles,
		multiple: multipleFiles || false,
		maxSize: maxUploadFileSize,
		onDrop: (acceptedFiles: any) => {
			setFiles(
				acceptedFiles.map((file: any) =>
					Object.assign(file, {
						preview: URL.createObjectURL(file),
					})
				)
			);
		},
		validator: (file: File): any => {
			if (file) {
				if (file.size > maxUploadFileSize)
					setErrorMessage(`${t("MAXIMUM_FILE_SIZE")} ${maxUploadFileSize / 1000000}MB`);
				else {
					setErrorMessage("");
				}
			}
		},
		onDropRejected: (fileRejections: FileRejection[], event: DropEvent) => {
			if (fileRejections.find((err) => err.errors.find((e) => e.code === "too-many-files"))) {
				setErrorMessage(`${t("MAXIMUM_FILE_COUNT")} ${maxFiles}`);
			}
		},
	});

	useEffect(() => {
		if (acceptedTypes && acceptedTypes.length > 0) {
			for (let k = 0; k < files.length; k++) {
				const file = files[k];
				if (!acceptedTypes.includes(file?.type)) {
					setErrorMessage(`${t("VALID_FILE_TYPES")} ${acceptedTypes.join(" , ")}`);
					return;
				}
			}
		}
		if (uploadRules && uploadRules.length > 0) {
			const uploadedFileRules = uploadRules.map((u) => ({
				...u,
				toBeUploadedFileCount: files.filter((f: any) => u.validTypes.includes(f.type)).length,
			}));
			const violatedFileRule = uploadedFileRules.find(
				(ufr) => ufr.toBeUploadedFileCount + ufr.currentQuantity > ufr.maxQuantity
			);
			if (!!violatedFileRule) {
				setErrorMessage(violatedFileRule.warningMessage);
				return;
			}
		}
		setErrorMessage("");
		if (files?.length === 1) {
			const reader = new FileReader();
			reader.onload = () => {
				mediaStore.uploadDocument(files[0]);
			};
			reader.readAsDataURL(files[0]);
		}
		if (files?.length > 1) {
			mediaStore.uploadDocumentList(files);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [files]);

	useEffect(() => {
		if (dropZoneError && dropZoneError !== "") {
			setErrorMessage(dropZoneError);
		}
	}, [dropZoneError]);

	return (
		<Flex direction="column">
			<DropZoneWrapper {...getRootProps({ className: "dropzone" })}>
				<input {...getInputProps()} />
				<Flex justifyContent="center">
					<AppText size="m">{t("DRAG_AND_DROP_A_FILE")}&nbsp;</AppText>
					<UnderlinedText>{t("BROWSE")}</UnderlinedText>
					<AppText size="m">&nbsp;{t("TO_UPLOAD")}</AppText>
				</Flex>

				{uploadedFileName && files.length === 0 && (
					<AppText margin="5px 0 0 0" size="m" weight="bold">
						{uploadedFileName}
					</AppText>
				)}
				{files.map((file: any, i: number) => (
					<AppText key={i} margin="5px 0 0 0" size="m" weight="bold">
						{file?.name}
					</AppText>
				))}
			</DropZoneWrapper>
			{errorMessage !== "" && <ErrorText>{errorMessage}</ErrorText>}
		</Flex>
	);
};
export default FileImporter;

const DropZoneWrapper = styled.div`
	display: flex;
	align-items: center;
	flex-direction: column;
	width: 100%;
	height: 130px;
	border: 1px dashed ${appTheme.colors.COLOR_BORDER_LIGHT};
	border-radius: 1px;
	background: ${appTheme.colors.COLOR_BACKGROUND_LIGHT};
	align-items: center;
	justify-content: center;
	text-align: center;
`;

const UnderlinedText = styled.div`
	font-size: ${appTheme.fontSizes.FONT_SIZE_M};
	color: ${appTheme.colors.COLOR_ACTION_BLUE};
	text-decoration: underline;
	cursor: pointer;
`;

const ErrorText = styled.div`
	display: flex;
	font-size: 0.75rem;
	color: ${appTheme.colors.COLOR_DANGER};
	margin: 0.3rem 0 0 0.15rem;
`;
